import { Component, OnInit, OnDestroy } from '@angular/core';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { DlgOkCancelComponent } from '../../../components/dlg-ok-cancel/dlg-ok-cancel.component';
import {
  OkCancelDialogData,
  okCancelDialogEnum,
} from '../../../components/dlg-ok-cancel/dlg-ok-cancel.model';
import { SignatureService } from '../../../services/signature.service';
import * as xWareSignatureModels from '../../../models/signature.models';
import { Subscription } from 'rxjs';

@Component({
  selector: 'xware-me-signature',
  templateUrl: './me-signature.component.html',
  styleUrls: ['./me-signature.component.scss'],
})
export class MeSignatureComponent implements OnInit, OnDestroy {
  bodyType: xWareSignatureModels.CoercionType;
  signaturePreviewInternal: SafeHtml;
  signaturePreviewExternal: SafeHtml;
  signatureNameInternal: string;
  signatureNameExternal: string;
  readonly noSignaturesMessage = 'No signatures assigned';
  emptyStateInternal = false;
  emptyStateExternal = false;

  to: string;
  loadingIndicatorInternal: boolean;
  loadingIndicatorExternal: boolean;
  insertInto: string;
  subPreviewInternal: Subscription;
  subPreviewExternal: Subscription;
  private loadingTimerInternal: ReturnType<typeof setTimeout>;
  private loadingTimerExternal: ReturnType<typeof setTimeout>;

  userEmailInformationInternal: Partial<xWareSignatureModels.UserEmailInformation>;
  userEmailInformationExternal: Partial<xWareSignatureModels.UserEmailInformation>;

  constructor(
    private signatureService: SignatureService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {
    const dtNow = new Date();
    this.to = sessionStorage.getItem('userPrincipalName') || '';

    this.userEmailInformationInternal = {
      baseURL: '',
      body: '',
      bodyType: xWareSignatureModels.CoercionType.html,
      from: sessionStorage.getItem('userPrincipalName') || '',
      to: [sessionStorage.getItem('userPrincipalName') || ''],
      subject: 'Preview Signature',
      date: '',
      isReplyEmail: 'false',
    };

    this.userEmailInformationExternal = { ...this.userEmailInformationInternal };
    this.userEmailInformationExternal.to = ['noone@example.com'];

    this.signaturePreviewInternal = '';
    this.signaturePreviewExternal = '';
  }

  ngOnInit() {
    this.updatePreview();
  }

  ngOnDestroy() {
    this.subPreviewInternal?.unsubscribe();
    this.subPreviewExternal?.unsubscribe();
    clearTimeout(this.loadingTimerInternal);
    clearTimeout(this.loadingTimerExternal);
  }

  private replaceImages(
    value: string,
    images: xWareSignatureModels.SignatureImageItem[] | undefined
  ): string {
    if (!images || images.length === 0) {
      return value;
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = value;
    const imageTags = tmp.querySelectorAll('img');
    imageTags.forEach((img) => {
      const src = img.getAttribute('src');
      if (!src) return;
      const toReplace = images.find((i) => i.imageCID === src.replace('cid:', ''));
      if (toReplace) {
        const ext = (toReplace.imageCID.split('.').pop() || '').toLowerCase();
        const mime = ext === 'gif' ? 'image/gif' : ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : 'image/png';
        img.setAttribute('src', `data:${mime};base64,` + toReplace.imageData);
      }
    });
    return tmp.innerHTML;
  }

  updatePreview(): void {
    this.signaturePreviewInternal = '';
    this.signaturePreviewExternal = '';
    this.emptyStateInternal = false;
    this.emptyStateExternal = false;

    this.loadingTimerInternal = setTimeout(() => {
      this.loadingIndicatorInternal = true;
    }, 500);

    this.loadingTimerExternal = setTimeout(() => {
      this.loadingIndicatorExternal = true;
    }, 500);

    this.subPreviewInternal = this.signatureService
      .getSignaturePreview(this.userEmailInformationInternal)
      .subscribe(
        (result: any) => {
          clearTimeout(this.loadingTimerInternal);
          if (this.isNoSignaturesAssigned(result)) {
            this.setEmptyState('internal');
            return;
          }
          if (result.isOK && result.signatures?.length > 0) {
            const first = result.signatures[0];
            if (first.signature) {
              let html = this.replaceImages(first.signature, first.images);
              html = html.replace(
                '&lt; Body text will be inserted here &gt;',
                '<span class="field-msg-light">-- Email body will end here --</span>'
              );
              this.signaturePreviewInternal = this.sanitizer.bypassSecurityTrustHtml(html);
              this.signatureNameInternal = first.configurationName ?? '';
            }
            this.emptyStateInternal = false;
            this.loadingIndicatorInternal = false;
          } else {
            this.handleError(result, 'internal');
          }
        },
        (error) => {
          clearTimeout(this.loadingTimerInternal);
          if (this.isNoSignaturesAssigned(error?.error)) {
            this.setEmptyState('internal');
            return;
          }
          this.showError(
            `Unable to load the internal signature for '${this.userEmailInformationInternal.from}'`
          );
        }
      );

    this.subPreviewExternal = this.signatureService
      .getSignaturePreview(this.userEmailInformationExternal)
      .subscribe(
        (result: any) => {
          clearTimeout(this.loadingTimerExternal);
          if (this.isNoSignaturesAssigned(result)) {
            this.setEmptyState('external');
            return;
          }
          if (result.isOK && result.signatures?.length > 0) {
            const first = result.signatures[0];
            if (first.signature) {
              let html = this.replaceImages(first.signature, first.images);
              html = html.replace(
                '&lt; Body text will be inserted here &gt;',
                '<span class="field-msg-light">-- Email body will end here --</span>'
              );
              this.signaturePreviewExternal = this.sanitizer.bypassSecurityTrustHtml(html);
              this.signatureNameExternal = first.configurationName ?? '';
            }
            this.emptyStateExternal = false;
            this.loadingIndicatorExternal = false;
          } else {
            this.handleError(result, 'external');
          }
        },
        (error) => {
          clearTimeout(this.loadingTimerExternal);
          if (this.isNoSignaturesAssigned(error?.error)) {
            this.setEmptyState('external');
            return;
          }
          this.showError(
            `Unable to load the external signature for '${this.userEmailInformationExternal.from}'`
          );
        }
      );
  }

  handleError(result: any, type: 'internal' | 'external'): void {
    const from =
      type === 'internal'
        ? this.userEmailInformationInternal.from
        : this.userEmailInformationExternal.from;
    const status = Number(result.status);
    if (status === 4) {
      this.showError(`Unable to find a ${type} signature for '${from}'`);
    } else {
      this.showError(`Unable to load the ${type} signature for '${from}'`);
    }
    if (type === 'internal') {
      this.loadingIndicatorInternal = false;
    } else {
      this.loadingIndicatorExternal = false;
    }
  }

  private isNoSignaturesAssigned(result: any): boolean {
    if (!result) {
      return false;
    }
    if (Array.isArray(result.signatures) && result.signatures.length === 0) {
      return true;
    }
    return (
      Number(result.status) === xWareSignatureModels.LOCALSIGNATURE_STATUS.STATUS_NOSIGANTURE
    );
  }

  private setEmptyState(type: 'internal' | 'external'): void {
    if (type === 'internal') {
      this.emptyStateInternal = true;
      this.signaturePreviewInternal = '';
      this.loadingIndicatorInternal = false;
    } else {
      this.emptyStateExternal = true;
      this.signaturePreviewExternal = '';
      this.loadingIndicatorExternal = false;
    }
  }

  cancelPreviewInternal() {
    clearTimeout(this.loadingTimerInternal);
    this.loadingIndicatorInternal = false;
    this.subPreviewInternal.unsubscribe();
  }

  cancelPreviewExternal() {
    clearTimeout(this.loadingTimerExternal);
    this.loadingIndicatorExternal = false;
    this.subPreviewExternal.unsubscribe();
  }

  showError(message: string): void {
    this.dialog.open(DlgOkCancelComponent, {
      width: '600px',
      data: {
        title: 'Signature Preview',
        content: message,
        oklabel: 'OK',
        cancellabel: '',
        dlgType: okCancelDialogEnum.error,
      } as OkCancelDialogData,
    });
    this.loadingIndicatorInternal = false;
    this.loadingIndicatorExternal = false;
  }
}
